import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import casesService from '../../services/casesService';
import { fetchCaseById } from './casesSlice';

/**
 * Case Events Slice
 *
 * "Event" is the harmonized product term for what the backend model
 * still calls a case-steps-tracker (its API paths remain
 * /cases/activity-logs — see casesService — since those aren't
 * user-facing). This slice used to be named activityLogsSlice; renamed
 * so the same feature isn't called Log / Event / Activity in different
 * places across the app (Phase 7).
 *
 * NOTE: Charge calculations are now handled by the backend.
 * When adding a case event, the backend returns:
 * - case_charges: { total_charges, billing_type, fee_breakdown }
 * - fee_breakdown includes: type, time_spent, is_supervisor, person_type, person_name, rate, charge
 *
 * Frontend responsibilities:
 * 1. Send event data: { case_id, lawyer_id, time_spent, description, created_at }
 * 2. Receive calculated response with case_charges
 * 3. Refresh case details to sync total_charges
 *
 * DO NOT calculate charges frontend-side anymore - backend now does all heavy lifting.
 */

interface CaseEvent {
  id: string;
  title?: string;
  timestamp: string;
  activity_description: string;
  lawyer_name: string;
  time_spent: number;
  status?: string;
  created_at: string;
}

interface CaseEventInput {
  case_id: number;
  title: string;
  lawyer_id?: string;
  time_spent: number;
  description: string;
  status?: string;
}

interface UpdateCaseEventInput {
  id: number;
  case_id: number;
  title: string;
  lawyer_id?: string | null;
  time_spent: number;
  description: string;
  status?: string;
}

interface CaseEventsState {
  logs: CaseEvent[];
  loading: boolean;
  error: string | null;
}

const initialState: CaseEventsState = {
  logs: [],
  loading: false,
  error: null,
};

export const fetchCaseEvents = createAsyncThunk(
  'caseEvents/fetchCaseEvents',
   async (case_id: number) => {
    const response = await casesService.getCaseEvents(case_id);
    return response;
  }
);

export const addCaseEvent = createAsyncThunk(
  'caseEvents/addCaseEvent',
  async (eventData: CaseEventInput, { dispatch, getState }) => {
    // Backend now handles all charge calculations automatically
    const response = await casesService.addCaseEvent(eventData);

    // If case is available, refresh case data to get updated total_charges
    // Backend response includes case_charges with total and fee breakdown
    const state = getState() as any;
    const currentCase = state.cases.currentCase;

    if (currentCase && currentCase.user_id) {
      // Refresh case details to sync with backend-calculated total_charges
      dispatch(fetchCaseById({ orderId: eventData.case_id.toString(), userId: currentCase.user_id }));
    }

    return response;
  }
);

export const updateCaseEvent = createAsyncThunk(
  'caseEvents/updateCaseEvent',
  async (eventData: UpdateCaseEventInput, { dispatch, getState }) => {
    const response = await casesService.updateCaseEvent(eventData);

    const state = getState() as any;
    const currentCase = state.cases.currentCase;

    if (currentCase && currentCase.user_id) {
      dispatch(fetchCaseById({ orderId: eventData.case_id.toString(), userId: currentCase.user_id }));
    }

    return response;
  }
);

export const deleteCaseEvent = createAsyncThunk(
  'caseEvents/deleteCaseEvent',
  async (params: { id: number; case_id: number }, { dispatch, getState }) => {
    const response = await casesService.deleteCaseEvent(params.id);

    const state = getState() as any;
    const currentCase = state.cases.currentCase;

    if (currentCase && currentCase.user_id) {
      dispatch(fetchCaseById({ orderId: params.case_id.toString(), userId: currentCase.user_id }));
    }

    return { ...response, id: params.id };
  }
);

export const fetchPublicCaseEvents = createAsyncThunk(
  'caseEvents/fetchPublicCaseEvents',
  async (params: { caseId: number; accessCode: string }) => {
    const response = await casesService.getPublicCaseEvents(params.caseId, params.accessCode);
    return response;
  }
);

const caseEventsSlice = createSlice({
  name: 'caseEvents',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLogs: (state) => {
      state.logs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Case Events
      .addCase(fetchCaseEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCaseEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.data;
      })
      .addCase(fetchCaseEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch events';
      })
      // Add Case Event
      .addCase(addCaseEvent.pending, (state) => {
        state.error = null;
      })
      .addCase(addCaseEvent.fulfilled, (state, action) => {
        // Backend now returns case_charges with total_charges calculated
        // Response structure includes: status, message, step (the event), case_charges
        const eventData = action.payload.step || action.payload;

        // Convert the response to match our CaseEvent interface
        const newEvent: CaseEvent = {
          id: eventData.id?.toString() || Date.now().toString(),
          title: eventData.title || '',
          timestamp: eventData.created_at || new Date().toISOString(),
          activity_description: eventData.description || '',
          lawyer_name: eventData.lawyer_name || 'Unknown',
          time_spent: eventData.time_spent || 0,
          created_at: eventData.created_at || new Date().toISOString(),
        };
        state.logs.unshift(newEvent);
      })
      .addCase(addCaseEvent.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add event';
      })
      // Update Case Event
      .addCase(updateCaseEvent.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCaseEvent.fulfilled, (state, action) => {
        const updated = action.payload.step;
        if (updated) {
          const idx = state.logs.findIndex((log: any) => String(log.id) === String(updated.id));
          if (idx !== -1) {
            state.logs[idx] = { ...state.logs[idx], ...updated };
          }
        }
      })
      .addCase(updateCaseEvent.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update event';
      })
      // Delete Case Event
      .addCase(deleteCaseEvent.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteCaseEvent.fulfilled, (state, action) => {
        state.logs = state.logs.filter((log: any) => String(log.id) !== String(action.payload.id));
      })
      .addCase(deleteCaseEvent.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete event';
      })
      // Fetch Public Case Events
      .addCase(fetchPublicCaseEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicCaseEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.data;
      })
      .addCase(fetchPublicCaseEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch public events';
      });
  },
});

export const { clearError, clearLogs } = caseEventsSlice.actions;
export default caseEventsSlice.reducer;
